import { type CSSProperties, type KeyboardEvent, type ReactNode, createContext, isValidElement, useCallback, useContext, useRef } from 'react';

/**
 * Two honest semantics for a horizontal tab strip (WCAG 1.3.1 / 4.1.2):
 *
 * - Inside a `<TabList>`: a real ARIA tab. `role="tab"`, `aria-selected`, roving
 *   tabindex, and the list handles Arrow/Home/End (the APG tabs pattern).
 * - Anywhere else (the common `<nav>` strip): a plain button that reports the
 *   current section with `aria-current`. Before this, the active tab was only a
 *   colour/border change, so a screen-reader user could not tell which section
 *   was showing.
 *
 * A bare `<div role="tablist">` around TabButtons is a critical axe failure
 * (aria-required-children: a tablist must own tabs). Use `<TabList>` instead;
 * `__tests__/TabButton.a11y.spec.tsx` keeps raw tablists from coming back.
 *
 * A TabList is half of the APG tabs pattern; the other half is the panel. Each
 * tab in a `<TabList idBase="x">` needs a `value`, and the page wraps the shown
 * content in `<TabPanel idBase="x" activeValue={...}>`. Every tab then has
 * `id="x-tab-<value>"` and `aria-controls="x-panel"`, and the one panel
 * container is `role="tabpanel"` labelled by the selected tab, so a screen
 * reader can move from a tab to its content. One shared panel container (instead
 * of one per tab) keeps every `aria-controls` resolvable while pages still mount
 * only the active tab's content (several tabs lazy-load their data).
 * The same spec fails a page that uses TabList without TabPanel or a TabButton
 * without `value`.
 */
const TabListContext = createContext<{ idBase: string } | null>(null);

export const tabIdFor = (idBase: string, value: string) => `${idBase}-tab-${value}`;
export const tabPanelIdFor = (idBase: string) => `${idBase}-panel`;

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  icon?: ReactNode;
  badge?: ReactNode;
  disabled?: boolean;
  className?: string;
  /**
   * Stable key of this tab inside a `<TabList>`; derives the tab's id and its
   * `aria-controls` (see `TabPanel`). Ignored outside a TabList.
   */
  value?: string;
}

export default function TabButton({ active, onClick, children, icon, badge, disabled = false, className = '', value }: TabButtonProps) {
  const tabList = useContext(TabListContext);
  const inTabList = tabList !== null;
  // If badge is a raw number/string, wrap it in a styled pill span so it
  // renders as its own flex item (with proper gap) rather than coalescing
  // with the label text. Custom ReactElements pass through unchanged so
  // callers can supply bespoke styling when needed.
  const renderedBadge =
    badge != null && !isValidElement(badge) ? (
      <span
        className={`text-xs px-1.5 py-0.5 rounded-full ${
          active
            ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400'
            : 'bg-surface-tertiary text-theme-secondary'
        }`}
      >
        {badge}
      </span>
    ) : (
      badge
    );

  const semantics = inTabList
    ? {
        role: 'tab' as const,
        'aria-selected': active,
        id: value !== undefined ? tabIdFor(tabList.idBase, value) : undefined,
        'aria-controls': value !== undefined ? tabPanelIdFor(tabList.idBase) : undefined,
        // Roving tabindex: only the selected tab is in the Tab order; arrows move between tabs.
        tabIndex: active ? 0 : -1,
      }
    : { 'aria-current': active ? ('true' as const) : undefined };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...semantics}
      className={`py-3 px-2 border-b-2 flex items-center gap-1.5 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--interactive-focus-ring)] disabled:opacity-50 disabled:cursor-not-allowed ${
        active
          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
      } ${className}`}
    >
      {icon}
      {children}
      {/* Literal space ensures the accessible name has a separator between
          the label text and the badge count (e.g. "Pending Acknowledgements 1"
          rather than "Pending Acknowledgements1") in addition to the CSS gap. */}
      {renderedBadge != null && ' '}
      {renderedBadge}
    </button>
  );
}

interface TabListProps {
  /** Accessible name of the tab set. Required: an unnamed tablist is announced as just "tab list". */
  label: string;
  /** Prefix for the tab and panel ids; must match the `<TabPanel idBase>` and be unique on the page. */
  idBase: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * ARIA tablist for TabButtons with automatic activation: ArrowLeft/ArrowRight
 * (wrapping), Home and End move focus and select the tab, as the WAI-ARIA
 * Authoring Practices tabs pattern describes.
 */
export function TabList({ label, idBase, children, className = '', style }: TabListProps) {
  const ref = useRef<HTMLDivElement>(null);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    const root = ref.current;
    if (!root) return;
    const tabs = Array.from(root.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'));
    const current = tabs.indexOf(document.activeElement as HTMLButtonElement);
    if (current < 0 || tabs.length === 0) return;
    let next: number;
    switch (e.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    tabs[next].focus();
    tabs[next].click();
  }, []);

  return (
    <TabListContext.Provider value={{ idBase }}>
      <div ref={ref} role="tablist" aria-label={label} onKeyDown={onKeyDown} className={className} style={style}>
        {children}
      </div>
    </TabListContext.Provider>
  );
}

interface TabPanelProps {
  /** Same `idBase` as the TabList it belongs to. */
  idBase: string;
  /** `value` of the selected TabButton; the panel is labelled by that tab. */
  activeValue: string;
  children: ReactNode;
  className?: string;
}

/**
 * The content container for a TabList: `role="tabpanel"`, the target of every
 * tab's `aria-controls`, labelled by the selected tab. `tabIndex=0` lets a
 * keyboard user reach the panel itself when its content starts with text
 * rather than a control (APG tabs pattern).
 */
export function TabPanel({ idBase, activeValue, children, className = '' }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={tabPanelIdFor(idBase)}
      aria-labelledby={tabIdFor(idBase, activeValue)}
      tabIndex={0}
      className={`focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--interactive-focus-ring)] ${className}`}
    >
      {children}
    </div>
  );
}
